<script setup>
import { computed } from 'vue';
import {
  getRowKeyValue,
  resolveRowState,
  sortItems,
  toggleSelectedKey,
} from './tableUtils';

const props = defineProps({
  headers: {
    type: Array,
    default: () => [],
  },
  items: {
    type: Array,
    default: () => [],
  },
  noDataMessage: {
    type: String,
    default: '',
  },
  loading: {
    type: Boolean,
    default: false,
  },
  loadingMessage: {
    type: String,
    default: 'Carregando...',
  },
  selectable: {
    type: Boolean,
    default: false,
  },
  selectedKeys: {
    type: Array,
    default: () => [],
  },
  rowKey: {
    type: [String, Function],
    default: 'id',
  },
  rowStateKey: {
    type: String,
    default: 'state',
  },
  sortBy: {
    type: String,
    default: '',
  },
  sortDirection: {
    type: String,
    default: 'asc',
    validator: value => ['asc', 'desc'].includes(value),
  },
});

const emit = defineEmits([
  'update:selectedKeys',
  'update:sortBy',
  'update:sortDirection',
  'sort',
  'rowSelect',
]);

const hasHeaderSlot = computed(() => !!props.headers.length);
const showHeaders = computed(
  () => hasHeaderSlot.value && (props.items.length > 0 || props.loading)
);
const sortedItems = computed(() =>
  sortItems(props.items, props.sortBy, props.sortDirection)
);
const colSpan = computed(
  () => props.headers.length + (props.selectable ? 1 : 0) || 1
);
const rowKeys = computed(() =>
  sortedItems.value.map((item, index) =>
    getRowKeyValue(item, props.rowKey, index)
  )
);
const allSelected = computed(
  () =>
    rowKeys.value.length > 0 &&
    rowKeys.value.every(key => props.selectedKeys.includes(key))
);
const selectAllRowsLabel = 'Selecionar todas as linhas';
const selectRowLabel = rowNumber => `Selecionar linha ${rowNumber}`;

const getHeaderLabel = header =>
  typeof header === 'string' ? header : header.label;
const getHeaderKey = header =>
  typeof header === 'string' ? header : header.key || header.value;
const isSortable = header =>
  Boolean(typeof header !== 'string' && header.sortable);
const getHeaderAlign = header =>
  typeof header === 'string' ? 'start' : header.align || 'start';
const getHeaderClass = header => ({
  'text-start': getHeaderAlign(header) === 'start',
  'text-center': getHeaderAlign(header) === 'center',
  'text-end': getHeaderAlign(header) === 'end',
});
const getCellValue = (item, header) => {
  if (typeof header === 'object' && typeof header.value === 'function') {
    return header.value(item);
  }

  return item?.[getHeaderKey(header)] ?? '';
};
const getRowKey = (item, index) => getRowKeyValue(item, props.rowKey, index);
const isRowSelected = (item, index) =>
  props.selectedKeys.includes(getRowKey(item, index));
const getRowState = item => resolveRowState(item, props.rowStateKey);
const rowStateClass = item =>
  ({
    error: 'bg-lais-danger/5 text-lais-danger',
    warning: 'bg-lais-warning/5 text-lais-text',
    success: 'bg-lais-success/5 text-lais-text',
    active: 'bg-lais-primary-50 text-lais-primary',
    selected: 'bg-lais-primary-50 text-lais-primary',
    default: '',
  })[getRowState(item)] || '';

const handleSort = header => {
  if (!isSortable(header)) return;

  const key = getHeaderKey(header);
  const nextDirection =
    props.sortBy === key && props.sortDirection === 'asc' ? 'desc' : 'asc';
  emit('update:sortBy', key);
  emit('update:sortDirection', nextDirection);
  emit('sort', { sortBy: key, sortDirection: nextDirection });
};

const toggleRow = (item, index) => {
  const key = getRowKey(item, index);
  const selectedKeys = toggleSelectedKey(props.selectedKeys, key);

  emit('update:selectedKeys', selectedKeys);
  emit('rowSelect', { item, key, selected: selectedKeys.includes(key) });
};

const toggleAllRows = () => {
  const selectedKeys = allSelected.value ? [] : rowKeys.value;

  emit('update:selectedKeys', selectedKeys);
};
</script>

<template>
  <div class="w-full">
    <table class="min-w-full table-auto divide-y divide-n-weak">
      <thead v-if="showHeaders" class="border-t border-n-weak">
        <tr>
          <th v-if="selectable" class="w-10 py-4 ltr:pr-4 rtl:pl-4 text-start">
            <input
              type="checkbox"
              class="size-4 rounded border-lais-border text-lais-primary focus:ring-lais-primary-50"
              :aria-label="selectAllRowsLabel"
              :checked="allSelected"
              @change="toggleAllRows"
            />
          </th>
          <th
            v-for="(header, index) in headers"
            :key="index"
            class="py-4 ltr:pr-4 rtl:pl-4 text-heading-3 text-n-slate-12 capitalize"
            :class="getHeaderClass(header)"
            :aria-sort="
              isSortable(header) && sortBy === getHeaderKey(header)
                ? sortDirection
                : undefined
            "
          >
            <button
              v-if="isSortable(header)"
              type="button"
              class="inline-flex items-center gap-1 font-semibold capitalize text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-lais-primary-50"
              @click="handleSort(header)"
            >
              <slot :name="`header-${index}`" :header="header">
                {{ getHeaderLabel(header) }}
              </slot>
              <span
                class="size-3"
                :class="
                  sortBy === getHeaderKey(header) && sortDirection === 'desc'
                    ? 'i-lucide-arrow-down'
                    : 'i-lucide-arrow-up'
                "
                aria-hidden="true"
              />
            </button>
            <slot v-else :name="`header-${index}`" :header="header">
              {{ getHeaderLabel(header) }}
            </slot>
          </th>
        </tr>
      </thead>
      <tbody class="divide-y divide-n-weak text-n-slate-11">
        <tr v-if="loading">
          <td
            :colspan="colSpan"
            class="py-12 text-center text-body-main !text-base text-n-slate-11"
          >
            {{ loadingMessage }}
          </td>
        </tr>
        <template v-else-if="sortedItems.length">
          <slot
            name="row"
            :items="sortedItems"
            :is-row-selected="isRowSelected"
            :toggle-row="toggleRow"
            :get-row-key="getRowKey"
            :get-row-state="getRowState"
            :row-state-class="rowStateClass"
          >
            <tr
              v-for="(item, rowIndex) in sortedItems"
              :key="getRowKey(item, rowIndex)"
              :class="[
                rowStateClass(item),
                {
                  'bg-lais-primary-50': isRowSelected(item, rowIndex),
                },
              ]"
            >
              <td v-if="selectable" class="w-10 py-3 ltr:pr-4 rtl:pl-4">
                <input
                  type="checkbox"
                  class="size-4 rounded border-lais-border text-lais-primary focus:ring-lais-primary-50"
                  :aria-label="selectRowLabel(rowIndex + 1)"
                  :checked="isRowSelected(item, rowIndex)"
                  @change="toggleRow(item, rowIndex)"
                />
              </td>
              <td
                v-for="(header, cellIndex) in headers"
                :key="cellIndex"
                class="py-3 ltr:pr-4 rtl:pl-4 text-body-main"
                :class="getHeaderClass(header)"
              >
                {{ getCellValue(item, header) }}
              </td>
            </tr>
          </slot>
        </template>
        <tr v-else-if="noDataMessage && !loading">
          <td
            :colspan="colSpan"
            class="py-20 text-center text-body-main !text-base text-n-slate-11"
          >
            {{ noDataMessage }}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
